import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../api';
import PartnerNavbar from '../../../components/PartnerNavbar';
import { Calendar, Plus, Clock, CheckCircle, XCircle, Edit2, Trash2, AlertCircle, Bell, RefreshCw, Lock } from 'lucide-react';

const STATUS_CONFIG = {
    pending: { label: 'Pending Review', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: Clock },
    approved: { label: 'Approved', color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: CheckCircle },
    rejected: { label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: XCircle },
    needs_edits: { label: 'Edits Required', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: Edit2 },
};

export default function PartnerDashboardPage() {
    const { user, isPartner, isAdmin, logout, loading: authLoading, showAlert } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const [filter, setFilter] = useState('all');
    const [notifications, setNotifications] = useState([]);
    const [showNotifDrop, setShowNotifDrop] = useState(false);
    const [resubmittingId, setResubmittingId] = useState(null);
    const notifRef = useRef(null);

    const canAccess = isPartner || isAdmin;

    useEffect(() => {
        if (canAccess) {
            loadEvents();
            loadNotifications();
        }
    }, [canAccess]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDrop(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const loadNotifications = async () => {
        try {
            const data = await api.getPartnerNotifications();
            setNotifications(data);
        } catch { }
    };

    const handleMarkAllRead = async () => {
        await api.markAllPartnerNotificationsRead().catch(() => { });
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const handleResubmit = (id) => {
        showAlert(
            'Resubmit Event',
            'Resubmit this event for review?',
            'info',
            async () => {
                try {
                    setResubmittingId(id);
                    await api.resubmitPartnerEvent(id);
                    await loadEvents();
                    showAlert('Success', 'Event resubmitted for review.', 'success');
                } catch (err) {
                    showAlert('Error', 'Failed to resubmit: ' + err.message, 'error');
                } finally {
                    setResubmittingId(null);
                }
            },
            true,
            'Resubmit'
        );
    };

    const loadEvents = async () => {
        try {
            setLoading(true);
            const data = await api.getMyEvents();
            setEvents(data);
        } catch (err) {
            console.error('Failed to load events:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        showAlert(
            'Delete Event',
            'Delete this pending event? This cannot be undone.',
            'error',
            async () => {
                try {
                    setDeletingId(id);
                    await api.deletePartnerEvent(id);
                    setEvents(prev => prev.filter(e => e.id !== id));
                    showAlert('Deleted', 'Event deleted successfully.', 'success');
                } catch (err) {
                    showAlert('Error', 'Failed to delete: ' + err.message, 'error');
                } finally {
                    setDeletingId(null);
                }
            },
            true,
            'Delete'
        );
    };

    if (authLoading) return <div style={{ padding: 80, textAlign: 'center' }}>Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!canAccess) return <Navigate to="/dashboard" replace />;

    const counts = {
        all: events.length,
        pending: events.filter(e => e.status === 'pending').length,
        approved: events.filter(e => e.status === 'approved').length,
        rejected: events.filter(e => e.status === 'rejected').length,
        needs_edits: events.filter(e => e.status === 'needs_edits').length,
    };
    const filtered = filter === 'all' ? events : events.filter(e => e.status === filter);
    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <>
            <PartnerNavbar />
            <main className="main">
                <div className="container" style={{ paddingTop: 40, paddingBottom: 80 }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
                    <div>
                        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>Partner Portal</h1>
                        <p style={{ color: 'var(--text-secondary)' }}>Welcome, {user.name} — manage your event submissions</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Notification Bell */}
                        <div ref={notifRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => { setShowNotifDrop(v => !v); }}
                                style={{
                                    background: unreadCount > 0 ? 'rgba(99,102,241,0.1)' : 'var(--bg-card)',
                                    border: `1px solid ${unreadCount > 0 ? 'rgba(99,102,241,0.3)' : 'var(--border)'}`,
                                    borderRadius: 10, padding: '8px 12px', cursor: 'pointer',
                                    color: unreadCount > 0 ? '#6366f1' : 'var(--text-secondary)',
                                    display: 'flex', alignItems: 'center', gap: 6, position: 'relative'
                                }}
                            >
                                <Bell size={18} />
                                {unreadCount > 0 && (
                                    <span style={{
                                        background: '#ef4444', color: '#fff', fontSize: '0.7rem',
                                        fontWeight: 700, borderRadius: '50%', width: 18, height: 18,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        position: 'absolute', top: -6, right: -6
                                    }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                                )}
                            </button>
                            {showNotifDrop && (
                                <div style={{
                                    position: 'absolute', top: '110%', right: 0, zIndex: 100,
                                    background: 'var(--bg-card)', borderRadius: 12, width: 320,
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)', border: '1px solid var(--border)', overflow: 'hidden'
                                }}>
                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Your Notifications</span>
                                        {unreadCount > 0 && (
                                            <button onClick={handleMarkAllRead} style={{ fontSize: '0.78rem', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>
                                        )}
                                    </div>
                                    {notifications.length === 0 ? (
                                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>All caught up!</div>
                                    ) : notifications.slice(0, 6).map(n => (
                                        <div key={n.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: n.read ? 'transparent' : 'rgba(99,102,241,0.05)' }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>{n.title}</div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{n.message}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <Link to="/change-password" className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Lock size={18} /> Change Password
                            </Link>
                            <Link to="/partner/submit" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Plus size={18} /> Submit New Event
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 32 }}>
                    {['all', 'pending', 'approved', 'rejected', 'needs_edits'].map(key => {
                        const cfg = key === 'all'
                            ? { label: 'Total Submitted', color: 'var(--primary)', bg: 'rgba(217,70,239,0.08)', icon: Calendar }
                            : STATUS_CONFIG[key];
                        if (!cfg) return null;
                        const Icon = cfg.icon;
                        return (
                            <div
                                key={key}
                                onClick={() => setFilter(key)}
                                className="card"
                                style={{ padding: 20, cursor: 'pointer', border: filter === key ? `2px solid ${cfg.color}` : '1px solid var(--border)', background: filter === key ? cfg.bg : undefined, transition: 'all 0.2s' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <Icon size={18} style={{ color: cfg.color }} />
                                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{cfg.label}</span>
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: cfg.color }}>{counts[key] ?? 0}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Events List */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Calendar size={18} style={{ color: 'var(--primary)' }} />
                        <h2 style={{ fontSize: 18, fontWeight: 600 }}>
                            {filter === 'all' ? 'All Submitted Events' : `${STATUS_CONFIG[filter]?.label} Events`}
                        </h2>
                    </div>

                    {loading ? (
                        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading your events...</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                            <Calendar size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
                            <h3 style={{ fontSize: 20, marginBottom: 8 }}>No events {filter !== 'all' ? `with status "${filter}"` : 'submitted yet'}</h3>
                            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>Submit your first event to get started</p>
                            <Link to="/partner/submit" className="btn btn-primary"><Plus size={16} /> Submit Event</Link>
                        </div>
                    ) : (
                        <div>
                            {filtered.map((event, i) => {
                                const cfg = STATUS_CONFIG[event.status] || STATUS_CONFIG.pending;
                                const Icon = cfg.icon;
                                return (
                                    <div key={event.id} style={{
                                        padding: '20px 24px',
                                        borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                                        display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap'
                                    }}>
                                        {/* Status badge */}
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px',
                                            borderRadius: 20, background: cfg.bg, color: cfg.color,
                                            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0
                                        }}>
                                            <Icon size={13} /> {cfg.label}
                                        </div>

                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{event.title}</h3>
                                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                <span>📅 {event.date ? new Date(event.date).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                                                <span>📍 {event.location}</span>
                                                <span>🏷️ {event.category}</span>
                                            </div>
                                            {/* Rejection reason */}
                                            {event.status === 'rejected' && event.rejection_reason && (
                                                <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', fontSize: 13, fontWeight: 600 }}>
                                                        <AlertCircle size={13} /> Rejection reason:
                                                    </div>
                                                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{event.rejection_reason}</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        {(event.status === 'pending' || event.status === 'needs_edits') && (
                                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                                <button
                                                    className="btn btn-secondary"
                                                    style={{ padding: '6px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                                                    onClick={() => navigate(`/partner/events/edit/${event.id}`)}
                                                >
                                                    <Edit2 size={13} /> Edit
                                                </button>
                                                {event.status === 'needs_edits' && (
                                                    <button
                                                        className="btn btn-primary"
                                                        style={{ padding: '6px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                                                        onClick={() => handleResubmit(event.id)}
                                                        disabled={resubmittingId === event.id}
                                                    >
                                                        <RefreshCw size={13} /> {resubmittingId === event.id ? 'Submitting...' : 'Resubmit'}
                                                    </button>
                                                )}
                                                {event.status === 'pending' && (
                                                    <button
                                                        className="btn"
                                                        style={{ padding: '6px 14px', fontSize: 13, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', gap: 6 }}
                                                        onClick={() => handleDelete(event.id)}
                                                        disabled={deletingId === event.id}
                                                    >
                                                        <Trash2 size={13} /> {deletingId === event.id ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </main>
        </>
    );
}
